import { skipToken } from '@reduxjs/toolkit/query';
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { Button, Typography } from '@/ui';

import { PageLoading } from '../PageState';
import { rpcErrorCode } from '../rpcError';

import type { McpDecision } from '@/store/api/mcpMemberApi';
import type { FC, ReactNode } from 'react';

import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useMcpMemberFlag } from '@/hooks/useMcpMemberFlag';
import {
  useDecideMcpAuthorizationRequestMutation,
  useGetMcpAuthorizationRequestQuery,
} from '@/store/api/mcpMemberApi';
import { safeSetLocationHref } from '@/utils/authRedirect';

const LOOPBACK_HOSTS = new Set(['localhost', '127.0.0.1', '[::1]']);
const EXPIRED_CODES = new Set(['request_not_found', 'request_unavailable']);

// Defense in depth: Moments builds redirect_url from the registered redirect
// URI. Only https, or loopback http for Claude Code, is followed.
export const isAllowedRedirect = (value: string): boolean => {
  try {
    const url = new URL(value);
    return (
      url.protocol === 'https:' ||
      (url.protocol === 'http:' && LOOPBACK_HOSTS.has(url.hostname))
    );
  } catch {
    return false;
  }
};

type Failure = 'expired' | 'error' | 'unavailable' | null;

const Card: FC<{ children: ReactNode }> = ({ children }) => (
  <main className="mx-auto w-full max-w-[560px] px-4 py-12">
    <div className="rounded-3xl bg-white p-6 shadow">{children}</div>
  </main>
);

const SHARE_ROWS = [
  {
    description:
      'What you realized, your strengths, your coach’s guidance, your action items, and the people involved.',
    key: 'notes',
    title: 'Coaching notes',
  },
  {
    description:
      'The story of each session, so your AI can look closer when a note isn’t enough.',
    key: 'reflections',
    title: 'Reflections',
  },
  {
    description:
      'Your AI can search your sessions for a short excerpt, like something you said. It never gets a full transcript.',
    key: 'transcripts',
    title: 'Transcript search',
  },
] as const;

const ConsentPage = () => {
  const [searchParams] = useSearchParams();
  const requestId = searchParams.get('request_id')?.trim() ?? '';
  const flag = useMcpMemberFlag();
  const { user } = useCurrentUser();
  const request = useGetMcpAuthorizationRequestQuery(
    flag.enabled && requestId ? { requestId } : skipToken,
  );
  const [decide, { isLoading: deciding }] =
    useDecideMcpAuthorizationRequestMutation();
  const [failure, setFailure] = useState<Failure>(null);

  const submit = async (decision: McpDecision) => {
    setFailure(null);
    try {
      const { redirect_url: redirectUrl } = await decide({
        decision,
        request_id: requestId,
      }).unwrap();
      if (isAllowedRedirect(redirectUrl)) {
        safeSetLocationHref(redirectUrl);
        return;
      }
      setFailure('error');
    } catch (error) {
      const code = rpcErrorCode(error);
      if (code === 'feature_unavailable') setFailure('unavailable');
      else if (code && EXPIRED_CODES.has(code)) setFailure('expired');
      else setFailure('error');
    }
  };
  const onDecide = (decision: McpDecision) => () => {
    submit(decision).catch(() => setFailure('error'));
  };
  // NEW COPY (prototype tone): failure lines.
  const failureLine =
    failure === 'expired' || failure === 'error' ? (
      <Typography className="mt-4 text-poppy-600" role="alert" variant="body2">
        {failure === 'expired'
          ? 'This link has expired. Start again from your AI.'
          : 'Something went wrong. Start again from your AI.'}
      </Typography>
    ) : null;

  if (flag.isLoading) return <PageLoading />;
  if (!requestId) {
    return (
      <Card>
        <Typography component="h1" variant="h5">
          This link isn’t complete.
        </Typography>
        <Typography className="mt-2 text-cement-400" variant="body1">
          Start again from your AI.
        </Typography>
      </Card>
    );
  }
  if (
    !flag.enabled ||
    failure === 'unavailable' ||
    rpcErrorCode(request.error) === 'feature_unavailable'
  ) {
    return (
      <Card>
        <Typography component="h1" variant="h5">
          Connecting an AI isn’t available for your account yet.
        </Typography>
        <Typography className="mt-2 text-cement-400" variant="body1">
          You can go back to your AI.
        </Typography>
        <div className="mt-6 flex justify-end">
          <Button
            loading={deciding}
            variant="outlined"
            onClick={onDecide('deny')}
          >
            Cancel
          </Button>
        </div>
        {failureLine}
      </Card>
    );
  }
  if (request.isLoading) return <PageLoading />;
  if (request.isError || !request.data) {
    // Contracts §8.7: only request_not_found / request_unavailable mean the
    // link is dead; a server or transport failure is not an expiry.
    const code = rpcErrorCode(request.error);
    if (!code || !EXPIRED_CODES.has(code)) {
      return (
        <Card>
          <Typography component="h1" variant="h5">
            Something went wrong. Start again from your AI.
          </Typography>
        </Card>
      );
    }
    return (
      <Card>
        <Typography component="h1" variant="h5">
          This link has expired.
        </Typography>
        <Typography className="mt-2 text-cement-400" variant="body1">
          Start again from your AI.
        </Typography>
      </Card>
    );
  }

  const {
    client_name: clientName,
    redirect_host: redirectHost,
    scopes,
    shares,
  } = request.data;
  return (
    <Card>
      {/* Consent phishing: client_name is self-declared, so the host the
          code goes to sits beside it, at full contrast (spec §6). */}
      <div aria-label="Requesting app" role="group">
        <Typography component="h1" variant="h5">
          Connect {clientName} to Mento
        </Typography>
        <Typography className="mt-1 text-cement-500" variant="body1">
          You’ll go back to <strong>{redirectHost}</strong>.
        </Typography>
      </div>
      <Typography className="mt-1 text-cement-400" variant="body2">
        Signed in as {user.firstName} ({user.email})
      </Typography>
      <Typography className="mt-5 font-semibold" variant="body1">
        {clientName} will be able to see:
      </Typography>
      <ul className="m-0 mt-2 flex list-none flex-col gap-3 p-0">
        {SHARE_ROWS.filter((row) => shares[row.key]).map((row) => (
          <li key={row.key}>
            <Typography className="font-semibold" variant="body1">
              {row.title}
            </Typography>
            <Typography className="text-cement-400" variant="body2">
              {row.description}
            </Typography>
          </li>
        ))}
      </ul>
      {scopes.includes('coaching:write') && (
        <Typography className="mt-4 text-cement-400" variant="body2">
          It can also add to your coaching notes, but only after you say yes in
          the chat.
        </Typography>
      )}
      <Typography className="mt-4 text-cement-400" variant="body2">
        Change or disconnect anytime.
      </Typography>
      <div className="mt-6 flex justify-end gap-2">
        <Button disabled={deciding} variant="text" onClick={onDecide('deny')}>
          Cancel
        </Button>
        <Button
          loading={deciding}
          variant="contained"
          onClick={onDecide('allow')}
        >
          Allow
        </Button>
      </div>
      {failureLine}
    </Card>
  );
};

export default ConsentPage;
